import React, { useEffect, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { orderService, Order } from '../services/orderService';

const OrderConfirmationPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const [order, setOrder] = useState<Order | null>((location.state as any)?.order ?? null);
    const [loading, setLoading] = useState(!order);

    useEffect(() => {
        if (!order && id) {
            orderService.getOrderById(id)
                .then(res => { setOrder(res.data.order); setLoading(false); })
                .catch(() => setLoading(false));
        }
    }, [id, order]);

    if (loading) return (
        <div style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Open Sans', sans-serif", color: '#64748b' }}>
            Loading order details…
        </div>
    );

    if (!order) return (
        <div style={{ minHeight: '50vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: "'Open Sans', sans-serif", textAlign: 'center', padding: 40 }}>
            <h2>Order not found</h2>
            <Link to="/user/orders" style={{ color: '#1565c0', fontWeight: 600 }}>View My Orders</Link>
        </div>
    );

    return (
        <div style={{ background: '#f8fafc', minHeight: '60vh', padding: '60px 0 80px', fontFamily: "'Open Sans', sans-serif" }}>
            <div className="container" style={{ maxWidth: 640, margin: '0 auto', padding: '0 20px' }}>

                {/* Success banner */}
                <div style={{ textAlign: 'center', marginBottom: 36 }}>
                    <div style={{
                        width: 72, height: 72, borderRadius: '50%', background: '#f0fdf4',
                        border: '3px solid #86efac', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', margin: '0 auto 20px',
                    }}>
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                        </svg>
                    </div>
                    <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: '#1a202c', margin: '0 0 8px' }}>Order Placed Successfully!</h1>
                    <p style={{ color: '#64748b', fontSize: '0.95rem', margin: 0 }}>
                        Thank you for your order. We'll confirm it shortly.
                    </p>
                </div>

                {/* Order ID card */}
                <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        <div>
                            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Order ID</span>
                            <p style={{ margin: '2px 0 0', fontSize: '1rem', fontWeight: 700, color: '#1565c0' }}>{order.orderId}</p>
                        </div>
                        <StatusBadge status={order.status} />
                    </div>
                    <p style={{ margin: '12px 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>
                        Placed on {new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                </div>

                {/* Delivery address */}
                <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20 }}>
                    <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 10px' }}>Deliver To</h3>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#374151', lineHeight: 1.7 }}>
                        <strong>{order.shippingAddress.name}</strong> · {order.shippingAddress.phone}<br />
                        {order.shippingAddress.flat}, {order.shippingAddress.street}
                        {order.shippingAddress.landmark ? `, ${order.shippingAddress.landmark}` : ''}<br />
                        {order.shippingAddress.city}, {order.shippingAddress.state} – {order.shippingAddress.pin}
                    </p>
                </div>

                {/* Items */}
                <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20 }}>
                    <h3 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 16px' }}>
                        Items ({order.items.reduce((s, i) => s + i.qty, 0)})
                    </h3>
                    {order.items.map((item, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: idx < order.items.length - 1 ? '1px solid #f1f5f9' : 'none', fontSize: '0.88rem', color: '#374151' }}>
                            <div>
                                <span style={{ fontWeight: 600 }}>{item.name}</span>
                                <span style={{ color: '#94a3b8', marginLeft: 8 }}>× {item.qty}</span>
                            </div>
                            <span style={{ fontWeight: 600 }}>₹{(item.price * item.qty).toLocaleString('en-IN')}</span>
                        </div>
                    ))}

                    <div style={{ marginTop: 16, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
                        <ConfirmRow label="Subtotal" value={`₹${order.subtotal.toLocaleString('en-IN')}`} />
                        <ConfirmRow
                            label={`GST @ ${order.gstRate}%`}
                            value={`₹${order.gstAmount.toLocaleString('en-IN')}`}
                            hint={order.gstRate === 5 ? 'Intra-state (Delhi)' : 'IGST (Inter-state)'}
                        />
                        <ConfirmRow label="Courier Charges" value={order.courierCharge === 0 ? 'FREE' : `₹${order.courierCharge.toLocaleString('en-IN')}`} />
                        <ConfirmRow label="Payment Method" value="Online Payment" />
                    </div>

                    <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: 14, display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1.05rem', color: '#16a34a' }}>
                        <span>Total Amount</span>
                        <span>₹{order.totalAmount.toLocaleString('en-IN')}</span>
                    </div>
                </div>

                {/* CTAs */}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <Link to="/user/orders" style={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '12px 20px', background: '#1565c0', color: '#fff',
                        borderRadius: 50, fontWeight: 700, fontSize: '0.95rem', textDecoration: 'none', textAlign: 'center',
                    }}>
                        View My Orders
                    </Link>
                    <Link to="/products" style={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '12px 20px', border: '1.5px solid #d1d5db', color: '#374151',
                        borderRadius: 50, fontWeight: 600, fontSize: '0.95rem', textDecoration: 'none', textAlign: 'center', background: '#fff',
                    }}>
                        Continue Shopping
                    </Link>
                </div>

            </div>
        </div>
    );
};

const ConfirmRow: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: '0.85rem', color: '#64748b' }}>
        <span>
            {label}
            {hint && <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginLeft: 4 }}>({hint})</span>}
        </span>
        <span style={{ fontWeight: 600, color: '#374151' }}>{value}</span>
    </div>
);

const STATUS_MAP: Record<string, { label: string; bg: string; color: string }> = {
    PENDING:   { label: 'Pending',   bg: '#fef9c3', color: '#854d0e' },
    CONFIRMED: { label: 'Confirmed', bg: '#dbeafe', color: '#1d4ed8' },
    SHIPPED:   { label: 'Shipped',   bg: '#e0f2fe', color: '#0369a1' },
    DELIVERED: { label: 'Delivered', bg: '#dcfce7', color: '#166534' },
    CANCELLED: { label: 'Cancelled', bg: '#fee2e2', color: '#991b1b' },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const s = STATUS_MAP[status] ?? { label: status, bg: '#f1f5f9', color: '#374151' };
    return (
        <span style={{ padding: '4px 14px', borderRadius: 50, background: s.bg, color: s.color, fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.05em' }}>
            {s.label}
        </span>
    );
};

export default OrderConfirmationPage;
